import React from "react";
import { Tooltip } from "@material-tailwind/react";
import useSubscriptionFeatures from "../hooks/useSubscriptionFeatures";
import { FEATURE_GATE_TOOLTIP } from "../constants/subscriptionFeatures";

/**
 * Wrap any element to render it visually-disabled with a tooltip explaining
 * the gate. Used to grey out CRM surfaces for tenants on BuildVizer Plus
 * without removing them — the affordance stays visible as an upsell hint.
 *
 * Usage:
 *   <DisabledWithTooltip feature={SUB_FEATURES.CRM}>
 *     <Button>Add Channel Partner</Button>
 *   </DisabledWithTooltip>
 *
 * When the feature is available the children render unmodified — no wrapper,
 * no opacity, no tooltip. When disabled, children are rendered inside a
 * pointer-events:none wrapper with reduced opacity and a hover tooltip.
 *
 * Props:
 *   feature  — dotted-path key from SUB_FEATURES (e.g. "modules.crm")
 *   tooltip  — optional override for the tooltip text
 *   reason   — optional second-line context shown after the main tooltip text
 *   wrap     — element type for the disabled wrapper (default: "span")
 */
const DisabledWithTooltip = ({
  feature,
  tooltip,
  reason,
  wrap: Wrap = "span",
  children,
  className = "",
}) => {
  const { isFeatureDisabled } = useSubscriptionFeatures();
  const { disabled } = isFeatureDisabled(feature);

  if (!disabled) return children;

  const tooltipText = tooltip || FEATURE_GATE_TOOLTIP;
  const fullText = reason ? `${tooltipText} — ${reason}` : tooltipText;

  return (
    <Tooltip content={fullText} placement="top">
      <Wrap
        aria-disabled="true"
        className={`pointer-events-none opacity-50 cursor-not-allowed ${className}`.trim()}
      >
        {children}
      </Wrap>
    </Tooltip>
  );
};

export default DisabledWithTooltip;
